// Troca de senha do próprio PlatformAdmin logado (painel Super Master,
// admin.gpsdrive.com.br) — pedido 2026-09-04: depois de trocar, o backend
// já revoga toda sessão ativa (ver platform-auth.controller.ts), então este
// dialog sempre desloga e manda pra /master/login em seguida, nunca deixa a
// aba atual seguir navegando com o token antigo em memória.
import { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "@tanstack/react-router";
import { Eye, EyeOff } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePlatformAuth } from "@/contexts/platform-auth-context";
import { gpsDrivePlatformAuthService } from "@/services/gpsdrive-platform-auth.service";
import { PlatformApiError } from "@/lib/api/gpsdrive-platform-client";

export function MasterChangePasswordDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { logout } = usePlatformAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleSave = async () => {
    if (!currentPassword || !newPassword) {
      toast.error("Preencha a senha atual e a nova senha");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("A nova senha precisa ter pelo menos 8 caracteres");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("As senhas não coincidem", { description: "Confira a confirmação da nova senha." });
      return;
    }
    setSaving(true);
    try {
      await gpsDrivePlatformAuthService.changePassword(currentPassword, newPassword);
      // A troca já revogou o refresh token no backend — logout() aqui só
      // limpa o estado local (admin/status) antes do redirect.
      await logout().catch(() => undefined);
      reset();
      onOpenChange(false);
      toast.success("Senha alterada. Entre novamente com a senha nova.");
      navigate({ to: "/master/login", replace: true });
    } catch (err) {
      toast.error(
        err instanceof PlatformApiError && err.status === 401
          ? "Senha atual incorreta"
          : "Não foi possível trocar a senha. Tente novamente.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Trocar senha</DialogTitle>
          <DialogDescription>
            Depois de salvar, sua sessão é encerrada — entre de novo com a senha nova.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <MasterPasswordField
            label="Senha atual"
            value={currentPassword}
            onChange={setCurrentPassword}
            show={showCurrent}
            onToggleShow={() => setShowCurrent((s) => !s)}
          />
          <MasterPasswordField
            label="Nova senha"
            value={newPassword}
            onChange={setNewPassword}
            show={showNew}
            onToggleShow={() => setShowNew((s) => !s)}
          />
          <MasterPasswordField
            label="Confirmar nova senha"
            value={confirmPassword}
            onChange={setConfirmPassword}
            show={showNew}
            onToggleShow={() => setShowNew((s) => !s)}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Salvando…" : "Salvar e sair"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MasterPasswordField({
  label,
  value,
  onChange,
  show,
  onToggleShow,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggleShow: () => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="relative">
        <Input
          type={show ? "text" : "password"}
          autoComplete="new-password"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="pr-10"
        />
        <button
          type="button"
          onClick={onToggleShow}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          aria-label={show ? "Ocultar senha" : "Mostrar senha"}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
